/** @format */

import {useEffect, useMemo, useState} from 'react';

import {Search as SearchIcon} from '@mui/icons-material';
import {Box, Table, TableCell, TableRow} from '@mui/material';
import {format} from 'date-fns';
import {Trans} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import BlueCheckbox from 'components/checkboxes/BlueCheckbox';
import Loading from 'components/loading/Loading';
import SidebarEdit from 'components/MediaplanSidebar/SidebarEdit';
import SidebarNew from 'components/MediaplanSidebar/SidebarNew';

import {
  useGetFeedsQuery,
  useGetTrackingFeedsQuery,
  useSaveTrackingFeedsSettingsMutation,
} from './graphql/__generated__';
import {
  ActionButton,
  ActionsContainer,
  BackButton,
  CheckboxCell,
  Controls,
  Footer,
  Header,
  SaveButton,
  SearchContainer,
  SearchField,
  StyledTableBody,
  StyledTableContainer,
  StyledTableHead,
  Subtitle,
  Title,
} from './styles';

const Feeds = () => {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || '';
  const isEdit = searchParams.get('edit');

  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFeeds, setSelectedFeeds] = useState<number[]>([]);

  const {data: feedsData, loading: feedsLoading} = useGetFeedsQuery({
    variables: {companyId},
    skip: !companyId,
  });

  const {data: trackingFeedsData, loading: trackingFeedsLoading} = useGetTrackingFeedsQuery({
    variables: {companyId},
    skip: !companyId,
  });

  const [saveTrackingFeedsSettings, {loading: savingSettings}] =
    useSaveTrackingFeedsSettingsMutation();

  const feeds = useMemo(() => feedsData?.currentUser?.company?.feeds || [], [feedsData]);
  const trackingFeeds = useMemo(
    () => trackingFeedsData?.currentUser?.company?.trackingFeeds || [],
    [trackingFeedsData],
  );

  useEffect(() => {
    if (trackingFeeds.length > 0) {
      setSelectedFeeds(trackingFeeds.map(feed => feed.id));
    }
  }, [trackingFeeds]);

  const filteredFeeds = useMemo(() => {
    if (!searchQuery.trim()) return feeds;

    const query = searchQuery.toLowerCase();
    return feeds.filter(
      feed =>
        feed.name.toLowerCase().includes(query) ||
        feed.accountName.toLowerCase().includes(query) ||
        feed.accountId.includes(query),
    );
  }, [feeds, searchQuery]);

  const handleSelectAll = () => {
    if (selectedFeeds.length === filteredFeeds.length) {
      setSelectedFeeds([]);
    } else {
      setSelectedFeeds(filteredFeeds.map(feed => feed.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedFeeds([]);
  };

  const handleFeedToggle = (feedId: number) => {
    setSelectedFeeds(prev =>
      prev.includes(feedId) ? prev.filter(id => id !== feedId) : [...prev, feedId],
    );
  };

  const handleSave = async () => {
    if (!companyId) return;

    const selectedFeedObjects = feeds
      .filter(feed => selectedFeeds.includes(feed.id))
      .map(feed => ({
        id: Number(feed.id),
        name: feed.name,
        accountId: feed.accountId,
        accountName: feed.accountName,
      }));

    await saveTrackingFeedsSettings({
      variables: {
        settings: {
          feeds: selectedFeedObjects,
        },
      },
    });
  };

  const handleBack = () => {
    navigate('/home/main/status');
  };

  const isLoading = feedsLoading || trackingFeedsLoading;
  const isAllSelected = selectedFeeds.length === filteredFeeds.length && filteredFeeds.length > 0;
  const isIndeterminate = selectedFeeds.length > 0 && selectedFeeds.length < filteredFeeds.length;

  return (
    <>
      {isEdit ? (
        <SidebarEdit activeStep={'FEEDS'} enableAll />
      ) : (
        <SidebarNew activeStep={'FEEDS'} enableAll />
      )}

      <Header>
        <Box data-testid='type'>
          <Title>
            <Trans>Фиды</Trans>
          </Title>
        </Box>
        <Box sx={{mt: 2}}>
          <Subtitle>
            <Trans>
              К проверке доступны фиды с типом источника данных «Ссылка». Фиды, загруженные как
              файл, не отображаются.
            </Trans>
          </Subtitle>
        </Box>
      </Header>

      <Controls>
        <SearchContainer>
          <SearchField
            placeholder='Поиск по названию, субаккаунту или номеру'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{color: '#828282', mr: 1}} />,
            }}
          />
        </SearchContainer>

        <ActionsContainer>
          <ActionButton onClick={handleSelectAll}>
            <Trans>Выбрать все</Trans>
          </ActionButton>
          <ActionButton onClick={handleClearSelection}>
            <Trans>Снять выделение</Trans>
          </ActionButton>
          <Subtitle>
            <Trans>
              {selectedFeeds.length ? `Выбрано: ${selectedFeeds.length}` : 'Ни один фид не выбран'}
            </Trans>
          </Subtitle>
        </ActionsContainer>
      </Controls>

      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell>
                <CheckboxCell>
                  <BlueCheckbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                  />
                </CheckboxCell>
              </TableCell>
              <TableCell>
                <Trans>Название фида</Trans>
              </TableCell>
              <TableCell>
                <Trans>Субаккаунт</Trans>
              </TableCell>
              <TableCell>
                <Trans>Номер</Trans>
              </TableCell>
              <TableCell>
                <Trans>Изменен</Trans>
              </TableCell>
              <TableCell>
                <Trans>Товаров в фиде</Trans>
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <StyledTableBody>
            {isLoading && <Loading />}
            {filteredFeeds.map(feed => (
              <TableRow key={feed.id}>
                <TableCell>
                  <CheckboxCell>
                    <BlueCheckbox
                      checked={selectedFeeds.includes(feed.id)}
                      onChange={() => handleFeedToggle(feed.id)}
                    />
                  </CheckboxCell>
                </TableCell>
                <TableCell>{feed.name}</TableCell>
                <TableCell>{feed.accountName}</TableCell>
                <TableCell>{feed.id}</TableCell>
                <TableCell>{format(feed.updatedAt, 'dd.MM.yyyy')}</TableCell>
                <TableCell>{feed.itemCount}</TableCell>
              </TableRow>
            ))}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>

      <Footer>
        <SaveButton onClick={handleSave} disabled={savingSettings || isLoading}>
          <Trans>{savingSettings ? 'Сохранение...' : 'Сохранить изменения'}</Trans>
        </SaveButton>
        <BackButton onClick={handleBack}>
          <Trans>На страницу проекта</Trans>
        </BackButton>
      </Footer>
    </>
  );
};

export default Feeds;
